import { AionCta } from "@/components/site/AionCta";
import { OG_ABOUT } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Home } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Raj Shamani Lifestyle — House, Cars, Mumbai Life & Routine";
const DESC =
  "What is actually known about Raj Shamani's lifestyle: where he lives, his work routine, travel and stage appearances — and which house and car claims online are unverified.";
const URL = "https://rajshamani.life/raj-shamani-lifestyle";
const OG = OG_ABOUT;

const facts = [
  ["Lives in", "Mumbai, Maharashtra"],
  ["From", "Indore, Madhya Pradesh"],
  ["Work base", "Mumbai — podcast studio and House of X"],
  ["Travel", "Frequent, for conferences, shoots and investor meetings"],
  ["House details", "Not publicly disclosed"],
  ["Car collection", "Not publicly disclosed"],
];

const faq = [
  {
    q: "Where does Raj Shamani live?",
    a: "Raj Shamani is based in Mumbai, where the Figuring Out podcast is recorded and House of X operates. He grew up in Indore, Madhya Pradesh.",
  },
  {
    q: "What is Raj Shamani's house like?",
    a: "He has not published details about his home, and no verified figures exist for its value or location beyond Mumbai. Numbers circulating on aggregator sites are estimates, not disclosures.",
  },
  {
    q: "What cars does Raj Shamani own?",
    a: "There is no confirmed car collection list. He has interviewed carmakers on the podcast — including the Bentley episode — which is where most of the car-related search interest comes from.",
  },
  {
    q: "What does Raj Shamani's daily routine look like?",
    a: "His public schedule is built around podcast recording blocks, content production, portfolio and House of X work, and speaking travel. He talks about batching recordings so interviews do not fragment the week.",
  },
  {
    q: "Is Raj Shamani's lifestyle funded by the podcast?",
    a: "The podcast is one of several income sources. Manufacturing through Shamani Industries, House of X, brand partnerships, speaking and angel investments all contribute — the income page breaks each one down.",
  },
];

export const Route = createFileRoute("/raj-shamani-lifestyle")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani lifestyle and Mumbai base", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Person",
            "@id": "https://rajshamani.life/about#person",
            name: "Raj Shamani",
            url: "https://rajshamani.life/about",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: LifestylePage,
});

function LifestylePage() {
  return (
    <div>
      <PageBreadcrumb current="Raj Shamani Lifestyle" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <Home size={14} /> Lifestyle
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Raj Shamani <span className="text-gradient-gold">lifestyle</span>, house &amp; cars
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Raj Shamani lives and works in Mumbai and grew up in Indore. Beyond that, most
            &quot;house&quot; and &quot;car collection&quot; figures online are estimates from
            aggregator sites, not anything he has disclosed. This page separates the two.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">What is actually confirmed</h2>
        <div className="overflow-hidden rounded-xl border border-border">
          <table className="w-full text-sm">
            <tbody>
              {facts.map(([k, v]) => (
                <tr key={k} className="border-b border-border last:border-0">
                  <th scope="row" className="text-left font-semibold px-5 py-4 bg-card w-1/3">
                    {k}
                  </th>
                  <td className="px-5 py-4 text-muted-foreground">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-8 text-muted-foreground max-w-3xl leading-relaxed">
          For the money side of the same question, the{" "}
          <Link to="/raj-shamani-income" className="text-gold underline underline-offset-4">
            income breakdown
          </Link>{" "}
          and{" "}
          <Link to="/raj-shamani-net-worth" className="text-gold underline underline-offset-4">
            net worth page
          </Link>{" "}
          explain where earnings come from and why published estimates disagree.
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <AionCta />
      <RelatedKeywords currentPath="/raj-shamani-lifestyle" />
      <RelatedPages pages={["aion", "about", "faq", "ventures", "book", "podcast"]} />
    </div>
  );
}
